import { css, cssManager, unsafeCSS, type CSSResult } from '@design.estate/dees-element';
import { cssSystemFontFamily, cssMonoFontFamily } from './00fonts.js';

// ============================================
// Theme refresh — notes
// ============================================
// This theme uses a modern, system-native design language: system colors with
// label-opacity tiers, larger corner radii, soft diffuse elevations,
// translucent materials (backdrop blur), a system type scale, and refined
// motion curves.
//
// Squircle: progressive enhancement via the CSS `corner-shape` property paired
// with the existing `border-radius` scale. CSS `superellipse(K)` uses exponent
// n = 2K, so `superellipse(1)` == `round` (the plain circular radius) and
// `superellipse(2)` == the `squircle` keyword (the classic n=4 squircle); K above
// 2 trends toward square (e.g. superellipse(4) is n=8 and reads edgy). We use
// `superellipse(2)` through the `--dees-corner-shape` token — the true squircle,
// which keeps almost the previous circular-radius footprint while adding
// continuous-curvature smoothing, so no radius bump is needed. Browsers without
// `corner-shape` keep `round` (today's radii). `corner-shape` must sit on the
// same element as its `border-radius`, so rounded-rectangle surfaces opt in with
// `corner-shape: var(--dees-corner-shape)`; pills/circles keep round. Tune the
// whole system from the single token value below.
//
// `cssManager.bdTheme()` (the light/dark bridge) is confined to this file by
// the ratchet in scripts/check-bdtheme-ratchet.cjs. Components consume the
// emitted `--dees-*` custom properties via `var()`, and use `:host([gobright])`
// for genuinely component-unique light/dark pairs.

// ============================================
// Theme Token Type Definitions
// ============================================

export interface IThemeColors {
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  /** Recessed shell/window base; content surfaces sit elevated above it (both themes). */
  bgCanvas: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  /** Quaternary label — placeholder / disabled text. */
  labelQuaternary: string;
  textWarm: string;
  textSuccess: string;
  textWarning: string;
  textError: string;
  borderDefault: string;
  borderSubtle: string;
  borderStrong: string;
  /** system fills — control backgrounds, toggles, segmented controls, chips. */
  fillPrimary: string;
  fillSecondary: string;
  fillTertiary: string;
  accentPrimary: string;
  accentSuccess: string;
  accentWarning: string;
  accentError: string;
  badgeDefaultBg: string;
  badgeDefaultFg: string;
  badgeSuccessBg: string;
  badgeSuccessFg: string;
  badgeWarningBg: string;
  badgeWarningFg: string;
  badgeErrorBg: string;
  badgeErrorFg: string;
}

export interface IThemeSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
}

export interface IThemeRadius {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  full: string;
}

export interface IThemeShadows {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export interface IThemeTransitions {
  fast: string;
  default: string;
  slow: string;
  slower: string;
}

/** motion curves. */
export interface IThemeEasings {
  /** General-purpose ease. */
  standard: string;
  /** Entering / appearing. */
  out: string;
  /** Exiting / disappearing. */
  in: string;
  /** "emphasized" spring-like curve (sheets, popovers). */
  spring: string;
  /** Subtle overshoot (toggles, small pops). */
  snappy: string;
}

/** A single design text style. */
export interface IThemeTypographyStyle {
  size: string;
  weight: string;
  lineHeight: string;
  letterSpacing: string;
}

export interface IThemeTypography {
  largeTitle: IThemeTypographyStyle;
  title1: IThemeTypographyStyle;
  title2: IThemeTypographyStyle;
  title3: IThemeTypographyStyle;
  headline: IThemeTypographyStyle;
  body: IThemeTypographyStyle;
  callout: IThemeTypographyStyle;
  subhead: IThemeTypographyStyle;
  footnote: IThemeTypographyStyle;
  caption1: IThemeTypographyStyle;
  caption2: IThemeTypographyStyle;
}

/** A translucent material (vibrancy) surface. */
export interface IThemeMaterial {
  /** Translucent background color. */
  bg: string;
  /** backdrop-filter value (blur + saturate); theme-agnostic. */
  filter: string;
  /** Solid fallback for `@supports not (backdrop-filter)`. */
  bgOpaque: string;
}

export interface IThemeMaterials {
  thin: IThemeMaterial;
  regular: IThemeMaterial;
  thick: IThemeMaterial;
}

export interface IThemeControlHeights {
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

/**
 * Terminal (xterm.js) palette. xterm renders to a <canvas>, so — like the
 * ECharts theme — it cannot read CSS custom properties and instead composes its
 * colors from this TypeScript source of truth (see
 * dees-workspace-terminal/terminal-tab-manager). The core background /
 * foreground / cursor are sourced from `colors` (bgPrimary / textPrimary /
 * accentPrimary) so they track the theme ladder automatically; this block only
 * carries the terminal-specific bits: the selection tint and the 16-color ANSI
 * palette (system colors).
 */
export interface IThemeTerminal {
  selectionBackground: string;
  black: string;
  red: string;
  green: string;
  yellow: string;
  blue: string;
  magenta: string;
  cyan: string;
  white: string;
  brightBlack: string;
  brightRed: string;
  brightGreen: string;
  brightYellow: string;
  brightBlue: string;
  brightMagenta: string;
  brightCyan: string;
  brightWhite: string;
}

export interface ITheme {
  colors: {
    light: IThemeColors;
    dark: IThemeColors;
  };
  spacing: IThemeSpacing;
  radius: IThemeRadius;
  shadows: IThemeShadows;
  /** Dark-theme shadow variants; falls back to `shadows` when omitted. */
  shadowsDark?: IThemeShadows;
  transitions: IThemeTransitions;
  easings: IThemeEasings;
  typography: IThemeTypography;
  materials: {
    light: IThemeMaterials;
    dark: IThemeMaterials;
  };
  controlHeights: IThemeControlHeights;
  terminal: {
    light: IThemeTerminal;
    dark: IThemeTerminal;
  };
}

// ============================================
// Default Theme Values (TypeScript Object)
// ============================================

export const themeDefaults: ITheme = {
  colors: {
    light: {
      bgPrimary: '#ffffff',                      // systemBackground
      bgSecondary: '#f2f2f7',                    // secondarySystemBackground / grouped
      bgTertiary: '#e9e9eb',                     // tertiary recess (headers, hovers)
      bgCanvas: '#f2f2f7',                       // recessed shell base (systemGroupedBackground)
      textPrimary: '#1d1d1f',                    // near-black label
      textSecondary: 'rgba(60, 60, 67, 0.60)',   // secondaryLabel
      textMuted: 'rgba(60, 60, 67, 0.30)',       // tertiaryLabel
      labelQuaternary: 'rgba(60, 60, 67, 0.18)', // quaternaryLabel (placeholder/disabled)
      textWarm: '#a2845e',                       // systemBrown
      textSuccess: '#248a3d',                    // systemGreen (accessible on light)
      textWarning: '#b25000',                    // systemOrange (accessible on light)
      textError: '#d70015',                      // systemRed (accessible on light)
      borderDefault: 'rgba(60, 60, 67, 0.29)',   // separator
      borderSubtle: 'rgba(60, 60, 67, 0.12)',
      borderStrong: 'rgba(60, 60, 67, 0.36)',    // opaqueSeparator-ish
      fillPrimary: 'rgba(120, 120, 128, 0.20)',  // systemFill
      fillSecondary: 'rgba(120, 120, 128, 0.16)',
      fillTertiary: 'rgba(118, 118, 128, 0.12)',
      accentPrimary: '#007aff',                  // systemBlue
      accentSuccess: '#34c759',                  // systemGreen
      accentWarning: '#ff9500',                  // systemOrange
      accentError: '#ff3b30',                    // systemRed
      badgeDefaultBg: '#e9e9eb',
      badgeDefaultFg: 'rgba(60, 60, 67, 0.60)',
      badgeSuccessBg: 'rgba(52, 199, 89, 0.15)',
      badgeSuccessFg: '#248a3d',
      badgeWarningBg: 'rgba(255, 149, 0, 0.15)',
      badgeWarningFg: '#b25000',
      badgeErrorBg: 'rgba(255, 59, 48, 0.15)',
      badgeErrorFg: '#d70015',
    },
    dark: {
      bgPrimary: '#000000',                       // systemBackground (base ladder) — content on true black
      bgSecondary: '#1c1c1e',                     // secondarySystemBackground / systemGray6 — cards, panels
      bgTertiary: '#2c2c2e',                      // tertiarySystemBackground / systemGray5 — headers, hovers
      bgCanvas: '#000000',                        // recessed shell base (true black, matches systemBackground)
      textPrimary: '#f5f5f7',                     // near-white label
      textSecondary: 'rgba(235, 235, 245, 0.60)', // secondaryLabel
      textMuted: 'rgba(235, 235, 245, 0.30)',     // tertiaryLabel
      labelQuaternary: 'rgba(235, 235, 245, 0.18)', // quaternaryLabel
      textWarm: '#ac8e68',                        // systemBrown (dark)
      textSuccess: '#30d158',                     // systemGreen (dark)
      textWarning: '#ff9f0a',                     // systemOrange (dark)
      textError: '#ff453a',                       // systemRed (dark)
      borderDefault: 'rgba(84, 84, 88, 0.60)',    // separator (dark)
      borderSubtle: 'rgba(84, 84, 88, 0.34)',
      borderStrong: 'rgba(84, 84, 88, 0.65)',
      fillPrimary: 'rgba(120, 120, 128, 0.36)',   // systemFill (dark)
      fillSecondary: 'rgba(120, 120, 128, 0.32)',
      fillTertiary: 'rgba(118, 118, 128, 0.24)',
      accentPrimary: '#0a84ff',                   // systemBlue (dark)
      accentSuccess: '#30d158',                   // systemGreen (dark)
      accentWarning: '#ff9f0a',                   // systemOrange (dark)
      accentError: '#ff453a',                     // systemRed (dark)
      badgeDefaultBg: '#2c2c2e',
      badgeDefaultFg: 'rgba(235, 235, 245, 0.60)',
      badgeSuccessBg: 'rgba(48, 209, 88, 0.18)',
      badgeSuccessFg: '#30d158',
      badgeWarningBg: 'rgba(255, 159, 10, 0.18)',
      badgeWarningFg: '#ff9f0a',
      badgeErrorBg: 'rgba(255, 69, 58, 0.18)',
      badgeErrorFg: '#ff453a',
    },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
  },
  radius: {
    xs: '4px',     // tags, tiny chips
    sm: '6px',     // inner elements, table cells
    md: '8px',     // small controls
    lg: '10px',    // standard control radius (buttons, inputs)
    xl: '14px',    // cards, tiles, popovers
    '2xl': '20px', // modals, sheets, large cards
    full: '999px', // pills
  },
  shadows: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
    sm: '0 2px 8px rgba(0, 0, 0, 0.06)',
    md: '0 6px 20px rgba(0, 0, 0, 0.08)',
    lg: '0 12px 40px rgba(0, 0, 0, 0.12)',
    xl: '0 24px 64px rgba(0, 0, 0, 0.16)',
  },
  shadowsDark: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.40)',
    sm: '0 2px 10px rgba(0, 0, 0, 0.45)',
    md: '0 8px 24px rgba(0, 0, 0, 0.55)',
    lg: '0 16px 48px rgba(0, 0, 0, 0.60)',
    xl: '0 28px 72px rgba(0, 0, 0, 0.66)',
  },
  transitions: {
    fast: '0.12s',
    default: '0.2s',
    slow: '0.3s',
    slower: '0.4s',
  },
  easings: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    spring: 'cubic-bezier(0.16, 1, 0.3, 1)',
    snappy: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  typography: {
    largeTitle: { size: '34px', weight: '700', lineHeight: '41px', letterSpacing: '-0.02em' },
    title1: { size: '28px', weight: '700', lineHeight: '34px', letterSpacing: '-0.02em' },
    title2: { size: '22px', weight: '600', lineHeight: '28px', letterSpacing: '-0.015em' },
    title3: { size: '20px', weight: '600', lineHeight: '25px', letterSpacing: '-0.01em' },
    headline: { size: '17px', weight: '600', lineHeight: '22px', letterSpacing: '-0.01em' },
    body: { size: '17px', weight: '400', lineHeight: '22px', letterSpacing: '-0.01em' },
    callout: { size: '16px', weight: '400', lineHeight: '21px', letterSpacing: '-0.01em' },
    subhead: { size: '15px', weight: '400', lineHeight: '20px', letterSpacing: '-0.006em' },
    footnote: { size: '13px', weight: '400', lineHeight: '18px', letterSpacing: '0' },
    caption1: { size: '12px', weight: '400', lineHeight: '16px', letterSpacing: '0' },
    caption2: { size: '11px', weight: '400', lineHeight: '13px', letterSpacing: '0.006em' },
  },
  materials: {
    light: {
      thin: { bg: 'rgba(255, 255, 255, 0.60)', filter: 'blur(20px) saturate(180%)', bgOpaque: '#f2f2f7' },
      regular: { bg: 'rgba(255, 255, 255, 0.72)', filter: 'blur(30px) saturate(180%)', bgOpaque: '#f2f2f7' },
      thick: { bg: 'rgba(255, 255, 255, 0.85)', filter: 'blur(40px) saturate(180%)', bgOpaque: '#ffffff' },
    },
    dark: {
      thin: { bg: 'rgba(30, 30, 30, 0.55)', filter: 'blur(20px) saturate(180%)', bgOpaque: '#1c1c1e' },
      regular: { bg: 'rgba(30, 30, 30, 0.72)', filter: 'blur(30px) saturate(180%)', bgOpaque: '#1c1c1e' },
      /* Thick = the most substantial surface (modals/sheets). In dark it sits
         at the elevated card tone (#1c1c1e) so it lifts off the black canvas —
         the old rgba(20,20,20)/#000000 pair sank below the ladder and dissolved
         into the scrim, while the brighter #2c2c2e tone read too light. */
      thick: { bg: 'rgba(28, 28, 30, 0.85)', filter: 'blur(40px) saturate(180%)', bgOpaque: '#1c1c1e' },
    },
  },
  // Control heights. keeps controls that sit together the same height, so
  // these map to roles rather than to individual components. Anchored to
  // macOS desktop metrics (regular push button ≈ 28px, large ≈ 32px), not
  // iOS touch targets — touch-first surfaces opt into xl:
  //   sm (24) — compact toolbar / table-row / dense controls
  //   md (28) — secondary / tighter contexts (macOS regular control)
  //   lg (32) — STANDARD interactive control: text fields, dropdowns, the
  //             segmented control and the default button all use this so they
  //             line up when placed alongside each other
  //   xl (40) — prominent CTA / touch-first contexts
  controlHeights: {
    sm: '24px',
    md: '28px',
    lg: '32px',
    xl: '40px',
  },
  // Terminal (xterm) — background/foreground/cursor are composed from `colors`
  // in terminal-tab-manager; only the selection tint + ANSI palette live here.
  terminal: {
    light: {
      selectionBackground: 'rgba(0, 122, 255, 0.25)',
      black: '#1d1d1f',
      red: '#d70015',
      green: '#248a3d',
      yellow: '#b25000',
      blue: '#0040dd',
      magenta: '#9f00a7',
      cyan: '#0071a4',
      white: '#8e8e93',
      brightBlack: '#6e6e73',
      brightRed: '#ff3b30',
      brightGreen: '#34c759',
      brightYellow: '#ff9500',
      brightBlue: '#007aff',
      brightMagenta: '#af52de',
      brightCyan: '#5ac8fa',
      brightWhite: '#1d1d1f',
    },
    dark: {
      selectionBackground: 'rgba(10, 132, 255, 0.32)',
      black: '#48484a',
      red: '#ff453a',
      green: '#30d158',
      yellow: '#ffd60a',
      blue: '#0a84ff',
      magenta: '#bf5af2',
      cyan: '#64d2ff',
      white: '#ebebf5',
      brightBlack: '#636366',
      brightRed: '#ff6961',
      brightGreen: '#30d158',
      brightYellow: '#ffd60a',
      brightBlue: '#64d2ff',
      brightMagenta: '#da8fff',
      brightCyan: '#70d7ff',
      brightWhite: '#ffffff',
    },
  },
};

// Shorthand aliases for CSS template usage
const l = themeDefaults.colors.light;
const d = themeDefaults.colors.dark;
const shadowsLight = themeDefaults.shadows;
const shadowsDark = themeDefaults.shadowsDark ?? themeDefaults.shadows;

/** Flips the y offsets of a box-shadow list so bars can cast upward
 * (e.g. a docked composer shadowing the content above it). */
const mirrorShadow = (shadow: string): string =>
  shadow.replace(/(^|,)(\s*\S+\s+)(-?\d+(?:\.\d+)?)px/g, (match, sep, x, y) => `${sep}${x}${-Number(y)}px`);
const matL = themeDefaults.materials.light;
const matD = themeDefaults.materials.dark;

/**
 * Theme-agnostic scalar scales (spacing, radius, transitions, easings, control
 * heights) generated straight from `themeDefaults` so the emitted CSS never
 * drifts from the TypeScript source of truth.
 */
const scaleVars = (prefix: string, obj: object): string =>
  Object.entries(obj as Record<string, string>)
    .map(([key, value]) => `--dees-${prefix}-${key}: ${value};`)
    .join('\n    ');

const scalarVars: CSSResult = unsafeCSS(
  [
    scaleVars('spacing', themeDefaults.spacing),
    scaleVars('radius', themeDefaults.radius),
    scaleVars('transition', themeDefaults.transitions),
    scaleVars('ease', themeDefaults.easings),
    scaleVars('control-height', themeDefaults.controlHeights),
  ].join('\n    '),
);

/**
 * Typography scale → granular `--dees-font-<style>-{size,weight,line,tracking}`
 * custom properties (e.g. `--dees-font-large-title-size`, `--dees-font-title1-size`).
 */
const typographyVars: CSSResult = unsafeCSS(
  (Object.entries(themeDefaults.typography) as [string, IThemeTypographyStyle][])
    .map(([name, style]) => {
      const k = name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
      return [
        `--dees-font-${k}-size: ${style.size};`,
        `--dees-font-${k}-weight: ${style.weight};`,
        `--dees-font-${k}-line: ${style.lineHeight};`,
        `--dees-font-${k}-tracking: ${style.letterSpacing};`,
      ].join('\n    ');
    })
    .join('\n    '),
);

// ============================================
// CSS Block for Component Import
// ============================================

/**
 * Default theme styles to be imported into every component's static styles array.
 * Provides CSS custom properties for colors, spacing, radius, shadows, transitions,
 * easings, typography, materials, and control heights.
 *
 * Usage:
 * ```typescript
 * import { themeDefaultStyles } from '../00theme.js';
 *
 * @customElement('my-component')
 * export class MyComponent extends DeesElement {
 *   public static styles = [
 *     themeDefaultStyles,
 *     cssManager.defaultStyles,
 *     css`...`
 *   ];
 * }
 * ```
 */
export const themeDefaultStyles: CSSResult = css`
  :host {
    /* ========================================
     * Typography — Family
     * ======================================== */
    --dees-font-family: ${cssSystemFontFamily};
    --dees-font-family-mono: ${cssMonoFontFamily};

    /* Dense-UI convenience sizes (dashboards/controls; the full design body is 17px).
       13px matches the macOS system control text size. */
    --dees-font-control-size: 13px;
    --dees-font-control-weight: 500;
    --dees-font-control-size-sm: 12px;

    /* ========================================
     * Typography — design scale
     * ======================================== */
    ${typographyVars}

    /* ========================================
     * Colors — Background
     * ======================================== */
    --dees-color-bg-primary: ${cssManager.bdTheme(l.bgPrimary, d.bgPrimary)};
    --dees-color-bg-secondary: ${cssManager.bdTheme(l.bgSecondary, d.bgSecondary)};
    --dees-color-bg-tertiary: ${cssManager.bdTheme(l.bgTertiary, d.bgTertiary)};
    --dees-color-bg-canvas: ${cssManager.bdTheme(l.bgCanvas, d.bgCanvas)};

    /* ========================================
     * Colors — Text / Label tiers
     * ======================================== */
    --dees-color-text-primary: ${cssManager.bdTheme(l.textPrimary, d.textPrimary)};
    --dees-color-text-secondary: ${cssManager.bdTheme(l.textSecondary, d.textSecondary)};
    --dees-color-text-muted: ${cssManager.bdTheme(l.textMuted, d.textMuted)};
    --dees-color-label-quaternary: ${cssManager.bdTheme(l.labelQuaternary, d.labelQuaternary)};
    --dees-color-text-warm: ${cssManager.bdTheme(l.textWarm, d.textWarm)};
    --dees-color-text-success: ${cssManager.bdTheme(l.textSuccess, d.textSuccess)};
    --dees-color-text-warning: ${cssManager.bdTheme(l.textWarning, d.textWarning)};
    --dees-color-text-error: ${cssManager.bdTheme(l.textError, d.textError)};

    /* ========================================
     * Colors — Border / Separator
     * ======================================== */
    --dees-color-border-default: ${cssManager.bdTheme(l.borderDefault, d.borderDefault)};
    --dees-color-border-subtle: ${cssManager.bdTheme(l.borderSubtle, d.borderSubtle)};
    --dees-color-border-strong: ${cssManager.bdTheme(l.borderStrong, d.borderStrong)};

    /* ========================================
     * Colors — System fills (control backgrounds)
     * ======================================== */
    --dees-color-fill-primary: ${cssManager.bdTheme(l.fillPrimary, d.fillPrimary)};
    --dees-color-fill-secondary: ${cssManager.bdTheme(l.fillSecondary, d.fillSecondary)};
    --dees-color-fill-tertiary: ${cssManager.bdTheme(l.fillTertiary, d.fillTertiary)};

    /* ========================================
     * Colors — Accent (system colors)
     * ======================================== */
    --dees-color-accent-primary: ${cssManager.bdTheme(l.accentPrimary, d.accentPrimary)};
    --dees-color-accent-success: ${cssManager.bdTheme(l.accentSuccess, d.accentSuccess)};
    --dees-color-accent-warning: ${cssManager.bdTheme(l.accentWarning, d.accentWarning)};
    --dees-color-accent-error: ${cssManager.bdTheme(l.accentError, d.accentError)};
    /* Foreground on accent / solid color fills — white in both themes (systemBlue/green/red etc.) */
    --dees-color-on-accent: #ffffff;

    /* ========================================
     * Colors — Badge
     * ======================================== */
    --dees-color-badge-default-bg: ${cssManager.bdTheme(l.badgeDefaultBg, d.badgeDefaultBg)};
    --dees-color-badge-default-fg: ${cssManager.bdTheme(l.badgeDefaultFg, d.badgeDefaultFg)};
    --dees-color-badge-success-bg: ${cssManager.bdTheme(l.badgeSuccessBg, d.badgeSuccessBg)};
    --dees-color-badge-success-fg: ${cssManager.bdTheme(l.badgeSuccessFg, d.badgeSuccessFg)};
    --dees-color-badge-warning-bg: ${cssManager.bdTheme(l.badgeWarningBg, d.badgeWarningBg)};
    --dees-color-badge-warning-fg: ${cssManager.bdTheme(l.badgeWarningFg, d.badgeWarningFg)};
    --dees-color-badge-error-bg: ${cssManager.bdTheme(l.badgeErrorBg, d.badgeErrorBg)};
    --dees-color-badge-error-fg: ${cssManager.bdTheme(l.badgeErrorFg, d.badgeErrorFg)};

    /* ========================================
     * Colors — Interactive States (system fills)
     * ======================================== */
    --dees-color-hover: ${cssManager.bdTheme('rgba(0, 0, 0, 0.04)', 'rgba(255, 255, 255, 0.06)')};
    --dees-color-active: ${cssManager.bdTheme('rgba(0, 0, 0, 0.07)', 'rgba(255, 255, 255, 0.09)')};
    --dees-color-pressed: ${cssManager.bdTheme('rgba(0, 0, 0, 0.10)', 'rgba(255, 255, 255, 0.13)')};
    --dees-color-row-hover: ${cssManager.bdTheme('rgba(0, 122, 255, 0.06)', 'rgba(10, 132, 255, 0.10)')};

    /* ========================================
     * Colors — Focus Ring (systemBlue)
     * ======================================== */
    --dees-color-focus-ring: ${cssManager.bdTheme('rgba(0, 122, 255, 0.45)', 'rgba(10, 132, 255, 0.55)')};

    /* ========================================
     * Control thumb — the elevated knob/thumb of a switch or segmented control,
     * lifted off its recessed track. White in light, a raised gray in dark.
     * ======================================== */
    --dees-color-control-thumb: ${cssManager.bdTheme('#ffffff', '#48484a')};
    --dees-shadow-control-thumb: ${cssManager.bdTheme(
      '0 1px 2px rgba(0, 0, 0, 0.16), 0 0 1px rgba(0, 0, 0, 0.12)',
      '0 1px 3px rgba(0, 0, 0, 0.5)',
    )};

    /* ========================================
     * Colors — Tooltip (inverted contrast, grouped grays)
     * ======================================== */
    --dees-color-tooltip-bg: ${cssManager.bdTheme('#1c1c1e', '#f2f2f7')};
    --dees-color-tooltip-fg: ${cssManager.bdTheme('#f5f5f7', '#1d1d1f')};

    /* ========================================
     * Colors — Link (systemBlue)
     * ======================================== */
    --dees-color-link: ${cssManager.bdTheme('#007aff', '#0a84ff')};
    --dees-color-link-hover: ${cssManager.bdTheme('#0062cc', '#409cff')};

    /* ========================================
     * Colors — Code
     * ======================================== */
    --dees-color-code-bg: ${cssManager.bdTheme('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.1)')};
    --dees-color-code-block-bg: ${cssManager.bdTheme('#f2f2f7', '#1c1c1e')};

    /* ========================================
     * Colors — Selection (systemBlue tint)
     * ======================================== */
    --dees-color-selection: ${cssManager.bdTheme('rgba(0, 122, 255, 0.2)', 'rgba(10, 132, 255, 0.3)')};

    /* ========================================
     * Colors — Scrim (the dim behind modals/sheets/overlays)
     * ======================================== */
    --dees-color-scrim: ${cssManager.bdTheme('rgba(0, 0, 0, 0.2)', 'rgba(0, 0, 0, 0.4)')};

    /* ========================================
     * Colors — Scrollbar
     * ======================================== */
    --dees-color-scrollbar-thumb: ${cssManager.bdTheme('rgba(0, 0, 0, 0.15)', 'rgba(255, 255, 255, 0.15)')};
    --dees-color-scrollbar-thumb-hover: ${cssManager.bdTheme('rgba(0, 0, 0, 0.25)', 'rgba(255, 255, 255, 0.25)')};

    /* ========================================
     * Spacing / Radius / Transitions / Easings / Control heights
     * (generated from themeDefaults)
     * ======================================== */
    ${scalarVars}

    /* Corner shape (squircle) — progressive enhancement; defaults to the
     * circular radius, upgraded to a subtle superellipse under @supports below. */
    --dees-corner-shape: round;

    /* ========================================
     * Shadow Elevation Scale (theme-aware)
     * ======================================== */
    --dees-shadow-xs: ${cssManager.bdTheme(shadowsLight.xs, shadowsDark.xs)};
    --dees-shadow-sm: ${cssManager.bdTheme(shadowsLight.sm, shadowsDark.sm)};
    --dees-shadow-md: ${cssManager.bdTheme(shadowsLight.md, shadowsDark.md)};
    --dees-shadow-lg: ${cssManager.bdTheme(shadowsLight.lg, shadowsDark.lg)};
    --dees-shadow-xl: ${cssManager.bdTheme(shadowsLight.xl, shadowsDark.xl)};
    /* upward-casting bar shadow (mirrored sm) for bottom-docked chrome */
    --dees-shadow-up-sm: ${cssManager.bdTheme(mirrorShadow(shadowsLight.sm), mirrorShadow(shadowsDark.sm))};

    /* ========================================
     * Materials / Vibrancy (theme-aware bg + opaque fallback; theme-agnostic filter)
     * ======================================== */
    --dees-material-thin-bg: ${cssManager.bdTheme(matL.thin.bg, matD.thin.bg)};
    --dees-material-thin-filter: ${unsafeCSS(matL.thin.filter)};
    --dees-material-thin-bg-opaque: ${cssManager.bdTheme(matL.thin.bgOpaque, matD.thin.bgOpaque)};
    --dees-material-regular-bg: ${cssManager.bdTheme(matL.regular.bg, matD.regular.bg)};
    --dees-material-regular-filter: ${unsafeCSS(matL.regular.filter)};
    --dees-material-regular-bg-opaque: ${cssManager.bdTheme(matL.regular.bgOpaque, matD.regular.bgOpaque)};
    --dees-material-thick-bg: ${cssManager.bdTheme(matL.thick.bg, matD.thick.bg)};
    --dees-material-thick-filter: ${unsafeCSS(matL.thick.filter)};
    --dees-material-thick-bg-opaque: ${cssManager.bdTheme(matL.thick.bgOpaque, matD.thick.bgOpaque)};
  }

  /* The true squircle (superellipse(2) == squircle keyword, n=4) keeps almost
     the circular-radius footprint with continuous-curvature smoothing where
     corner-shape is supported. */
  @supports (corner-shape: superellipse(2)) {
    :host {
      --dees-corner-shape: superellipse(2);
    }
  }

  /* systemBlue text selection across every component shadow tree. */
  ::selection {
    background-color: var(--dees-color-selection);
  }
`;
